import React, { useCallback, useEffect, useMemo, useState } from "react";

import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import ModeEditOutlineOutlinedIcon from "@mui/icons-material/ModeEditOutlineOutlined";
import { Box, Button, Collapse, IconButton, Stack, Typography } from "@mui/material";
import { toast } from "react-toastify";

import { fetchClientCharacteristics } from "actions/clientCharacteristics";
import { RoleName } from "constants";
import { useClientCharacteristics } from "hooks/useClientCharacteristics";
import { apiClient } from "services/api";
import { useUserRoleSelector } from "store/selectors/user-selectors";
import type { ClientCharacteristic } from "types/domain";

import { CharacteristicModal } from "../../AdminPanel/Characteristics/CharacteristicModal";
import { DeleteConfirmationModal } from "../../AdminPanel/Characteristics/DeleteConfirmationModal";
import { ClientCharacteristicsChip } from "./ClientCharacteristicsChip";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface ClientCharacteristicsStripProps {
  clientId: number;
}

// ---------------------------------------------------------------------------
// Chip configuration
// ---------------------------------------------------------------------------

interface ChipConfigItem {
  label: string;
  apiKey: string;
}

const CHIP_CONFIG: ChipConfigItem[] = [
  { label: "Type", apiKey: "Type" },
  { label: "RM", apiKey: "RM" },
  { label: "Domicile", apiKey: "Domicile" },
  { label: "Primary Currency", apiKey: "Primary Currency" },
];

// ---------------------------------------------------------------------------
// Collapse initialization utilities
// ---------------------------------------------------------------------------

/**
 * Get initial expanded state from localStorage with fallback to mobile detection
 */
function getInitialExpanded(clientId: number): boolean {
  const key = `clientCharacteristicsCollapsed_${clientId}`;
  try {
    const stored = localStorage.getItem(key);
    if (stored !== null) {
      return stored === "true";
    }
    // No saved preference — check if mobile viewport
    const isMobile =
      typeof window !== "undefined" &&
      window.matchMedia?.("(max-width: 767px)")?.matches === true;
    return !isMobile;
  } catch (err) {
    // localStorage disabled or throwing — fall back to expanded
    console.warn("Failed to read localStorage for collapse state:", err);
    return true;
  }
}

/**
 * Persist expanded state to localStorage with error handling
 */
function persistExpandedState(clientId: number, expanded: boolean): void {
  const key = `clientCharacteristicsCollapsed_${clientId}`;
  try {
    localStorage.setItem(key, String(expanded));
  } catch (err) {
    // Silently fail if localStorage is disabled
    console.warn("Failed to persist collapse state to localStorage:", err);
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const ClientCharacteristicsStrip: React.FC<ClientCharacteristicsStripProps> = ({
  clientId,
}) => {
  // =========================================================================
  // Hooks & Selectors
  // =========================================================================
  const role = useUserRoleSelector();
  const { data } = useClientCharacteristics(clientId);

  // =========================================================================
  // UI State Management
  // =========================================================================
  const [expanded, setExpanded] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);

  // =========================================================================
  // Dynamic Characteristics State
  // =========================================================================
  const [dynamicCharacteristics, setDynamicCharacteristics] = useState<ClientCharacteristic[]>([]);
  const [characteristicsLoading, setCharacteristicsLoading] = useState<boolean>(false);
  const [fetchCounter, setFetchCounter] = useState<number>(0);

  // =========================================================================
  // Modal State Management
  // =========================================================================
  const [isAddModalOpen, setIsAddModalOpen] = useState<boolean>(false);
  const [selectedCharacteristic, setSelectedCharacteristic] = useState<ClientCharacteristic | null>(null);
  const [characteristicToDelete, setCharacteristicToDelete] = useState<ClientCharacteristic | null>(null);

  // =========================================================================
  // Optimistic Updates State
  // =========================================================================
  const [optimisticValues, setOptimisticValues] = useState<Record<string, string | null>>({});

  // =========================================================================
  // Computed Values (Memoized)
  // =========================================================================
  
  /**
   * Determine user permissions for editing
   */
  const canEdit = useMemo(() => {
    const isAdmin = role?.name === RoleName.Admin;
    const isAnalyst = role?.name === RoleName.Analyst;
    return isAdmin || isAnalyst;
  }, [role]);

  /**
   * Get characteristic value with optimistic updates
   */
  const getValue = useCallback(
    (apiKey: string): string | null => {
      // Check optimistic values first
      if (apiKey in optimisticValues) {
        return optimisticValues[apiKey] as string | null;
      }
      // Fallback to server data: { data: { [characteristicName]: value } }
      const source = (data as any)?.data ?? {};
      const raw = source[apiKey] ?? null;
      return raw as string | null;
    },
    [data, optimisticValues]
  );

  // =========================================================================
  // Effects
  // =========================================================================

  /**
   * Persist collapse state to localStorage
   */
  useEffect(() => {
    persistExpandedState(clientId, expanded);
  }, [expanded, clientId]);

  /**
   * Fetch dynamic characteristic definitions from server
   */
  useEffect(() => {
    let cancelled = false;

    const loadCharacteristics = async () => {
      setCharacteristicsLoading(true);
      try {
        const characteristicsData = await fetchClientCharacteristics();
        if (!cancelled) {
          setDynamicCharacteristics(characteristicsData);
        }
      } catch (err) {
        if (!cancelled) {
          console.error("Failed to fetch characteristic definitions:", err);
          toast.error("Failed to load characteristics");
        }
      } finally {
        if (!cancelled) {
          setCharacteristicsLoading(false);
        }
      }
    };

    loadCharacteristics();

    return () => {
      cancelled = true;
    };
  }, [fetchCounter]);

  // =========================================================================
  // Event Handlers
  // =========================================================================

  /**
   * Save characteristic value with optimistic update
   *
   * Characteristics-bar pattern: each chip manages its own open/close state.
   * The parent only orchestrates data persistence and error recovery.
   */
  const handleSave = useCallback(
    async (apiKey: string, prevValue: string | null, newValue: string) => {
      // Step 1: Optimistic update (immediate UI feedback)
      setOptimisticValues((prev) => ({ ...prev, [apiKey]: newValue }));

      try {
        // Step 2: Persist to server
        await apiClient.patch(
          `/api/v0.0.3/clients/${clientId}/characteristics`,
          { [apiKey]: newValue }
        );
        toast.success("Saved");
      } catch (err: any) {
        // Step 3: Revert optimistic update on error
        setOptimisticValues((prev) => ({ ...prev, [apiKey]: prevValue }));

        // Step 4: Show appropriate error message
        const status = err?.response?.status;
        if (status === 403) {
          toast.error("Insufficient permissions");
        } else if (err?.message?.includes?.("timeout")) {
          toast.error("Save timed out — please try again");
        } else {
          console.error("Failed to save characteristic:", err);
          toast.error("Save failed — please try again");
        }
      }
    },
    [clientId]
  );

  /**
   * Toggle collapse state
   */
  const handleToggle = useCallback(() => {
    setExpanded((prev) => !prev);
  }, []);

  /**
   * Toggle edit mode
   */
  const handleEditClick = useCallback(() => {
    setIsEditing((prev) => !prev);
  }, []);

  /**
   * Open add characteristic modal
   */
  const handleOpenAddModal = useCallback(() => {
    setIsAddModalOpen(true);
  }, []);

  /**
   * Edit existing characteristic
   */
  const handleEditCharacteristic = useCallback((char: ClientCharacteristic) => {
    setSelectedCharacteristic(char);
  }, []);

  /**
   * Close modal and optionally refetch characteristics
   */
  const handleCloseModal = useCallback((shouldRefetch: boolean) => {
    setIsAddModalOpen(false);
    setSelectedCharacteristic(null);
    if (shouldRefetch) {
      setFetchCounter((c) => c + 1);
    }
  }, []);

  /**
   * Request deletion of selected characteristic
   */
  const handleDeleteRequest = useCallback(() => {
    if (selectedCharacteristic) {
      setCharacteristicToDelete(selectedCharacteristic);
    }
  }, [selectedCharacteristic]);

  /**
   * Complete deletion and refresh
   */
  const handleDeleteComplete = useCallback((_id: number) => {
    setCharacteristicToDelete(null);
    setSelectedCharacteristic(null);
    setFetchCounter((c) => c + 1);
  }, []);

  /**
   * Cancel deletion
   */
  const handleDeleteCancel = useCallback(() => {
    setCharacteristicToDelete(null);
  }, []);

  // =========================================================================
  // Render
  // =========================================================================
  return (
    <Box
      data-testid="characteristics-strip"
      data-expanded={String(expanded)}
      sx={{
        borderRadius: "8px",
        bgcolor: "white",
        marginBottom: "16px",
        boxShadow: "0 1px 2px 0 #1212171a",
        transition: "0.5s",
        overflow: "hidden",
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        sx={{ padding: "16px", alignItems: "center" }}
      >
        <Stack direction="row" gap="10px" alignItems="center" flexWrap="nowrap" sx={{ minWidth: 0, overflow: "hidden" }}>
          {/* Toggle button — Characteristics-bar pattern: IconButton with rotating chevron */}
          <IconButton
            data-testid="toggle-characteristics"
            onClick={handleToggle}
          >
            <KeyboardArrowRightIcon
              sx={{
                transform: expanded ? "rotate(90deg)" : "none",
                transition: "transform 0.3s",
                cursor: "pointer",
              }}
            />
          </IconButton>

          {/* Static characteristic chips — each chip manages its own popover state */}
          {CHIP_CONFIG.map(({ label, apiKey }) => {
            const currentValue = getValue(apiKey);
            return (
              <ClientCharacteristicsChip
                key={label}
                label={label}
                value={currentValue}
                role={role}
                onSave={(newValue) =>
                  handleSave(apiKey, currentValue, newValue)
                }
              />
            );
          })}

          {/* Dynamic characteristics from the server */}
          {!characteristicsLoading && dynamicCharacteristics.map((char) => {
            const currentValue = getValue(char.name);
            if (isEditing) {
              // Edit mode: chip opens CharacteristicModal for definition editing
              return (
                <Button
                  key={char.id}
                  data-testid={`dynamic-chip-${char.name}`}
                  sx={{
                    color: "#496682",
                    padding: "0 10px",
                    minWidth: "auto",
                    fontSize: "15px",
                    backgroundColor: "#edf1f9",
                    minHeight: "28px",
                    maxHeight: "28px",
                    textTransform: "none",
                    cursor: "pointer",
                    "&:hover": { backgroundColor: "#dce3f0" },
                  }}
                  disableElevation
                  onClick={() => handleEditCharacteristic(char)}
                >
                  <Stack direction="row" gap="5px">
                    <Typography sx={{ color: "#496682", fontSize: "12px" }}>
                      {char.name}:
                    </Typography>
                    <Typography sx={{ color: "#071F35", fontSize: "12px" }}>
                      {currentValue || "N/A"}
                    </Typography>
                  </Stack>
                </Button>
              );
            }
            // View mode: standard self-contained ClientCharacteristicsChip
            return (
              <ClientCharacteristicsChip
                key={char.id}
                label={char.name}
                value={currentValue}
                role={role}
                onSave={(newValue) =>
                  handleSave(char.name, currentValue, newValue)
                }
              />
            );
          })}

        </Stack>

        {canEdit && (
          <Stack direction="row" alignItems="center">
            <IconButton
              data-testid="edit-icon"
              onClick={handleEditClick}
              sx={{
                fontSize: "15px"
              }}
            >
              <ModeEditOutlineOutlinedIcon />
            </IconButton>
            {isEditing && (
              <IconButton
                data-testid="add-characteristic-icon"
                onClick={handleOpenAddModal}
                sx={{
                  fontSize: "15px"
                }}
              >
                <AddOutlinedIcon />
              </IconButton>
            )}
          </Stack>
        )}

      </Stack>

      {/* Expanded detail view — Characteristics-bar Collapse pattern (timeout=500) */}
      <Collapse in={expanded} timeout={500} unmountOnExit>
        <Box
          sx={{
            borderTop: "1px solid #dae0e2",
            p: "16px",
            bgcolor: "#fafbfb",
          }}
        >
          <Stack direction="row" flexWrap="wrap" gap="12px">
            {CHIP_CONFIG.map(({ label, apiKey }) => {
              const currentValue = getValue(apiKey);
              return (
                <Box key={apiKey} sx={{ minWidth: "160px" }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#898989",
                      fontSize: "11px",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    {label}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: "#0a3142",
                      fontSize: "13px",
                      mt: "2px",
                    }}
                  >
                    {currentValue || "\u2014"}
                  </Typography>
                </Box>
              );
            })}
            {/* Dynamic characteristics in expanded view */}
            {!characteristicsLoading && dynamicCharacteristics.map((char) => {
              const currentValue = getValue(char.name);
              return (
                <Box key={char.id} sx={{ minWidth: "160px" }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#898989",
                      fontSize: "11px",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    {char.name}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: "#0a3142",
                      fontSize: "13px",
                      mt: "2px",
                    }}
                  >
                    {currentValue || "\u2014"}
                  </Typography>
                </Box>
              );
            })}
          </Stack>
        </Box>
      </Collapse>

      {/* Characteristic Modal — Add mode */}
      <CharacteristicModal
        open={isAddModalOpen}
        characteristic={null}
        onClose={handleCloseModal}
      />

      {/* Characteristic Modal — Edit mode */}
      {selectedCharacteristic && (
        <CharacteristicModal
          open={Boolean(selectedCharacteristic)}
          characteristic={selectedCharacteristic}
          onClose={handleCloseModal}
          onDelete={handleDeleteRequest}
        />
      )}

      {/* Delete Confirmation Modal */}
      {characteristicToDelete && (
        <DeleteConfirmationModal
          open={Boolean(characteristicToDelete)}
          characteristic={characteristicToDelete}
          onClose={handleDeleteCancel}
          onDeleteComplete={handleDeleteComplete}
        />
      )}
    </Box>
  );
};
